import type { Tagged } from 'type-fest';
import { SystemMutableEntity as SME } from '../../../common';
import { UniqueShortId } from '../../../common/id';
import { Identifier } from '../../../common/identifier';
import type { SystemCommandContext } from '../../common';
import { Event } from '../../event';
import { IdentityLevel } from '..';
import { _DefaultLevels } from './default';

const uuidGenerator = UniqueShortId.GetFactory(12);

/**
 * Identity Level のデフォルト値を定義するマスターテーブル。
 * troppus 側で中央管理するグローバルなデフォルト値。
 * PKは identifierType + eventType。
 */
export type _Config = SME & {
  id: _Config.Id;
  identifierType: Identifier.Type;
  eventType: Event.TypeExpression;
  defaultLevel: IdentityLevel;
};

export namespace _Config {
  export const Default = _DefaultLevels;

  export type Id = Tagged<UniqueShortId, 'identityLevelConfigId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  /** Config が見つからない場合の保守的なデフォルト */
  export const FALLBACK_LEVEL: IdentityLevel = 'L1';

  export namespace Construct {
    export type Props = {
      id: string;
      identifierType: string;
      eventType: string;
      defaultLevel: string;
    } & SME.Props;
  }
  export const Construct = (props: Construct.Props): _Config => {
    return {
      id: Id.FromString(props.id),
      identifierType: Identifier.Type.FromString(props.identifierType),
      eventType: Event.TypeExpression.FromString(props.eventType),
      defaultLevel: IdentityLevel.FromString(props.defaultLevel),
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id' | 'createdAt' | 'updatedAt'>;
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }
  export const Create = (params: Create.Params): _Config => {
    const { context, props } = params;
    const { now } = context;
    return Construct({
      id: Id.Generate(),
      ...props,
      ...SME.Create({ date: now }),
    });
  };
}
